import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CameraAlt as CameraAltIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from '@mui/icons-material';

// Modular subcomponents (kept within inventory folder)
import CameraCapture from './aiAddItem/CameraCapture';
import LoadingAnalysis from './aiAddItem/LoadingAnalysis';
import ProductImages from './aiAddItem/ProductImages';
import DetectionInfo from './aiAddItem/DetectionInfo';
import CustomerStockSection from './aiAddItem/CustomerStockSection';
import ScrapedProductInfo from './aiAddItem/ScrapedProductInfo';

// CRM
import { useCRM } from '../../contexts/CRMContext';
import api from '../../utils/api';
import { suggestFurnitureCategory, normalizeCategory } from '../../utils/categorize';

const AiAddItemModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  selectedLocation,
  isSubmitting = false 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { createCustomer } = useCRM();
  
  // AI Detection State
  const [aiDetecting, setAiDetecting] = useState(false);
  const [aiDetectionResult, setAiDetectionResult] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [placementMode, setPlacementMode] = useState(null); // 'customer' | 'stock' | 'store_front'
  const [aiCameraOpen, setAiCameraOpen] = useState(false);
  const [aiCapturedImage, setAiCapturedImage] = useState(null);
  const [existingItem, setExistingItem] = useState(null);
  const [selectedWebImages, setSelectedWebImages] = useState([]);
  const [webImages, setWebImages] = useState([]);
  const [currentSearchQuery, setCurrentSearchQuery] = useState('');
  const [customSearchQuery, setCustomSearchQuery] = useState('');
  const [isSearchingImages, setIsSearchingImages] = useState(false);
  const [scrapeRefreshTick, setScrapeRefreshTick] = useState(0); // trigger Ashley scrape
  const [gpsLocation, setGpsLocation] = useState(null);
  const [linking, setLinking] = useState({ customerId: null, orderId: null });
  const [showResults, setShowResults] = useState(false);
  const [customerForm, setCustomerForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [aiFormData, setAiFormData] = useState({
    quantity: 1,
    pricing: { cost: '', retail: '' },
    positioning: { x: 0, y: 0, z: 0 },
    boxDimensions: { length: '', width: '', height: '' }
  });
  const [editableSku, setEditableSku] = useState('');
  const [scrapedProductData, setScrapedProductData] = useState(null);
  const [warehousePhotos, setWarehousePhotos] = useState([]);
  const [warehouseCameraOpen, setWarehouseCameraOpen] = useState(false);
  const [isMarkingReceivedForOrder, setIsMarkingReceivedForOrder] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [requiredPhotos, setRequiredPhotos] = useState(1);
  const [baseStock, setBaseStock] = useState(0);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [activeSlotIndex, setActiveSlotIndex] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualSku, setManualSku] = useState('');
  const [isManualSearching, setIsManualSearching] = useState(false);
  
  // Refs
  const fileInputRef = useRef(null);
  const warehouseVideoRef = useRef(null);
  const warehouseCanvasRef = useRef(null);

  // Start warehouse camera when modal opens
  useEffect(() => {
    if (warehouseCameraOpen) {
      startWarehouseCamera();
    }
  }, [warehouseCameraOpen]);

  // Detect if marking item as received for order
  useEffect(() => {
    const isReceivingForOrder = placementMode === 'customer' && linking.orderId && linking.customerId;
    setIsMarkingReceivedForOrder(isReceivingForOrder);
  }, [placementMode, linking.orderId, linking.customerId]);

  // Update required photos and initialize slots when quantity changes
  useEffect(() => {
    const newQuantity = Math.max(baseStock + 1, parseInt(aiFormData.quantity) || 1);
    setRequiredPhotos(newQuantity);
    
    // Initialize warehouse photo slots
    setWarehousePhotos(prev => {
      const newPhotos = Array(newQuantity).fill(null);
      
      // Preserve existing photos in their slots
      for (let i = 0; i < Math.min(prev.length, newQuantity); i++) {
        if (prev[i]) {
          newPhotos[i] = prev[i];
        }
      }
      
      // Add existing photos from database to the beginning slots
      if (existingPhotos.length > 0) {
        for (let i = 0; i < Math.min(existingPhotos.length, baseStock); i++) {
          newPhotos[i] = {
            url: existingPhotos[i].url || existingPhotos[i].data,
            isExisting: true,
            slotIndex: i,
            id: existingPhotos[i].id
          };
        }
      }
      
      return newPhotos;
    });
  }, [aiFormData.quantity, baseStock, existingPhotos]);

  // Warehouse camera functions
  function startWarehouseCamera() {
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'environment' }
    }).then(stream => {
      if (warehouseVideoRef.current) {
        warehouseVideoRef.current.srcObject = stream;
      }
    }).catch(error => {
      console.error('Error accessing warehouse camera:', error);
      alert('Could not access camera. Please check permissions.');
    });
  }

  function captureWarehousePhoto() {
    if (warehouseVideoRef.current && warehouseCanvasRef.current && activeSlotIndex !== null) {
      const canvas = warehouseCanvasRef.current;
      const video = warehouseVideoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          
          // Update the specific slot with the photo
          setWarehousePhotos(prev => {
            const newPhotos = [...prev];
            newPhotos[activeSlotIndex] = { 
              url, 
              blob, 
              slotIndex: activeSlotIndex,
              timestamp: new Date().toISOString()
            };
            return newPhotos;
          });
          
          // Close camera and reset active slot
          setWarehouseCameraOpen(false);
          setActiveSlotIndex(null);
          
          // Stop camera stream
          const stream = video.srcObject;
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }
          
          console.log(`Photo captured for slot ${activeSlotIndex + 1}`);
        }
      }, 'image/jpeg', 0.8);
    }
  }

  function closeWarehouseCamera() {
    if (warehouseVideoRef.current?.srcObject) {
      const stream = warehouseVideoRef.current.srcObject;
      stream.getTracks().forEach(track => track.stop());
    }
    setWarehouseCameraOpen(false);
    setActiveSlotIndex(null); // Reset active slot when closing
  }

  // Function to open camera for specific slot
  function openCameraForSlot(slotIndex) {
    setActiveSlotIndex(slotIndex);
    setWarehouseCameraOpen(true);
  }

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setAiDetectionResult(null);
      setSuggestions(null);
      setPlacementMode(null);
      setAiCapturedImage(null);
      setExistingItem(null);
      setSelectedWebImages([]);
      setWebImages([]);
      setCurrentSearchQuery('');
      setCustomSearchQuery('');
      setLinking({ customerId: null, orderId: null });
      setShowResults(false);
      setCustomerForm({ firstName: '', lastName: '', email: '', phone: '' });
      setEditableSku('');
      setScrapedProductData(null);
      setAiFormData({
        title: '',
        description: '',
        brand: '',
        category: '',
        quantity: 1,
        pricing: { retail: '', cost: '', margin: 0 },
        positioning: { x: 0, y: 0, z: 0 },
        boxDimensions: { length: '', width: '', height: '' }
      });
      setWarehousePhotos([]);
      setExistingPhotos([]);
      setBaseStock(0);
      setActiveSlotIndex(null);
      setWarehouseCameraOpen(false);
      setCurrentPhotoIndex(0);
      setRequiredPhotos(1);
      setManualMode(false);
      setManualSku('');
      setIsManualSearching(false);
    }
  }, [open]);

  // Always sync scraped data into detected product when available
  useEffect(() => {
    if (!scrapedProductData) return;
    if (!scrapedProductData.title && !scrapedProductData.description) return;

    console.log('[Title Sync] Updating from scraped data:', scrapedProductData.title);
    
    setAiDetectionResult(prev => {
      if (!prev || typeof prev !== 'object') return { detectedProduct: {} };
      const updated = { ...(prev.detectedProduct || {}) };
      
      // Always update if we have new scraped data
      if (scrapedProductData.title) {
        updated.name = scrapedProductData.title;
      }
      if (scrapedProductData.description) {
        updated.description = scrapedProductData.description;
      }
      if (scrapedProductData.model) {
        updated.model = scrapedProductData.model;
      }
      if (scrapedProductData.brand) {
        updated.brand = scrapedProductData.brand;
      }
      
      const result = { ...prev, detectedProduct: updated };
      console.log('[Title Sync] Updated detection result:', result);
      return result;
    });
    
    // Force Google Images to refresh with new title
    setCurrentSearchQuery('');
  }, [scrapedProductData]);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (aiCapturedImage?.url) {
        URL.revokeObjectURL(aiCapturedImage.url);
      }
    };
  }, [aiCapturedImage]);

  // Effect to re-run image search when SKU changes
  useEffect(() => {
    const performImageSearch = async () => {
      if (!aiDetectionResult?.detectedProduct || !editableSku) return;

      const detected = aiDetectionResult.detectedProduct;
      const queryParts = [detected.brand, editableSku, detected.model, detected.name].filter(Boolean);
      const newSearchQuery = queryParts.join(' ');

      if (newSearchQuery && newSearchQuery !== currentSearchQuery) {
        console.log(`[Image Search] SKU changed. New query: "${newSearchQuery}"`);
        setIsSearchingImages(true);
        try {
          const response = await api.get('/ai-detection/google-images', {
            params: { query: newSearchQuery }
          });
          if (response.data?.success && response.data?.images) {
            setWebImages(response.data.images);
            setCurrentSearchQuery(newSearchQuery);
            setCustomSearchQuery(newSearchQuery); // Update the search bar display
            // Auto-select first 3 new images
            const autoSelected = response.data.images
              .slice(0, 3)
              .map(img => img.url || img);
            setSelectedWebImages(autoSelected);
          }
        } catch (error) {
          console.error('Failed to fetch images on SKU change:', error);
        } finally {
          setIsSearchingImages(false);
        }
      }
    };

    // Debounce the image search to avoid excessive API calls while typing
    const handler = setTimeout(() => {
      performImageSearch();
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [editableSku, aiDetectionResult, currentSearchQuery]);

  // Initialize/Update placement mode from suggestions
  useEffect(() => {
    if (suggestions && !placementMode) {
      setPlacementMode(suggestions.isStock ? 'stock' : 'customer');
    }
  }, [suggestions, placementMode]);


  const getGpsLocation = async () => {
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser.');
      return null;
    }
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          enableHighAccuracy: true,
        });
      });
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        altitude: position.coords.altitude,
        accuracy: position.coords.accuracy,
      };
    } catch (geoError) {
      console.log('GPS location not available:', geoError);
      // Do not alert here, as it can be intrusive if the user intentionally denied it.
      return null;
    }
  };

  const handleClose = () => {
    setAiCameraOpen(false);
    setAiDetecting(false);
    setAiDetectionResult(null);
    setSuggestions(null);
    setPlacementMode(null);
    setAiCapturedImage(null);
    setExistingItem(null);
    setShowResults(false);
    setLinking({ customerId: null, orderId: null });
    setManualMode(false);
    setManualSku('');
    setIsManualSearching(false);
    onClose();
  };

  const startAiCamera = async () => {
    // Open camera component without requesting location
    setAiCameraOpen(true);
  };

  // Photo capture is handled by CameraCapture component

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const imageUrl = URL.createObjectURL(file);
      setAiCapturedImage({ blob: file, url: imageUrl });
      
      // Debug logging for file upload
      console.log('🔍 File upload debug:', { file, fileType: file.type, fileSize: file.size });
      
      // Perform AI detection
      performAiDetection(file, gpsLocation);
    } else {
      alert('Please select a valid image file.');
    }
  };

  const performAiDetection = async (imageBlob, capturedGpsLocation) => {
    setAiDetecting(true);
    
    try {
      // Debug logging
      console.log('🔍 performAiDetection called with:', { imageBlob, capturedGpsLocation });
      console.log('🔍 selectedLocation:', selectedLocation);
      
      // Validate selectedLocation exists (now optional)
      // if (!selectedLocation || !selectedLocation._id) {
      //   console.error('❌ No selected location or location ID');
      //   alert('Please select a location first');
      //   setAiDetecting(false);
      //   return;
      // }
      
      // 1) Extract label fields with OpenAI Vision first (customer name, SKU, product description)
      try {
        const fdLabel = new FormData();
        fdLabel.append('image', imageBlob, 'label-image.jpg');
        const { data: labelRes } = await api.post('/labels/extract', fdLabel);
        if (labelRes?.success && labelRes?.data) {
          const label = labelRes.data;
          const customerName = label.customer_name?.toString().trim() || '';
          const sku = label.sku?.toString().trim();
          // Map to existing suggestions shape
          const mapped = {
            isStock: false,
            customerName: customerName,
            skuCandidates: sku ? [sku] : [],
            matchedCustomer: null,
            matchedOrder: null,
            matchReasons: {}
          };
          setSuggestions(mapped);
          // Prefill customer form if name present
          if (customerName) {
            const parts = customerName.split(/\s+/);
            setCustomerForm({
              firstName: parts[0] || '',
              lastName: parts.slice(1).join(' ') || '',
              email: '',
              phone: ''
            });
          }
          console.log('✅ Label extraction result:', labelRes);
        }
      } catch (labelErr) {
        console.warn('Label extraction failed (non-blocking):', labelErr?.response?.data || labelErr.message);
      }
      
      // Prepare form data
      const formData = new FormData();
      formData.append('image', imageBlob, 'furniture-image.jpg');
      if (selectedLocation?._id) {
        formData.append('locationId', selectedLocation._id);
      }
      formData.append('enhancedDetection', 'true'); // Enable enhanced furniture recognition
      
      console.log('🔍 AI Detection Request Debug:');
      console.log('Image blob:', imageBlob);
      console.log('Location ID:', selectedLocation._id);
      console.log('Selected location object:', selectedLocation);
      
      const metadata = {
        gpsLocation: capturedGpsLocation,
        deviceInfo: {
          make: navigator.userAgent.includes('iPhone') ? 'Apple' : 'Unknown',
          model: navigator.userAgent,
          orientation: window.screen?.orientation?.angle || 0
        },
        timestamp: new Date().toISOString(),
        requestType: 'furniture_recognition'
      };
      
      formData.append('metadata', JSON.stringify(metadata));
      
      // Log all FormData entries in detail
      console.log('🔍 FormData contents:');
      for (let pair of formData.entries()) {
        console.log(pair[0] + ':', pair[1]);
        if (pair[0] === 'image') {
          console.log('  File name:', pair[1].name);
          console.log('  File size:', pair[1].size);
          console.log('  File type:', pair[1].type);
        }
      }
      
      // Also log FormData as object for easier inspection
      const formDataObj = {};
      for (let pair of formData.entries()) {
        formDataObj[pair[0]] = pair[1];
      }
      console.log('🔍 FormData as object:', formDataObj);

      // Add placementMode to formData to check existence in the right inventory type
      formData.append('placementMode', placementMode || 'stock');
      formData.append('customerId', linking.customerId || '');
      
      // Call enhanced AI product detection API (using centralized api client)
      const { data: result } = await api.post('/ai-detection/detect-product', formData);
      
      if (result.success) {
        // Map the new service response to expected format
        const responseData = result.data || {};
        const detectionData = responseData.detectionResult || {};
        const extractedInfo = responseData.extractedInfo || {};
        const shippingInfo = responseData.shippingInfo || {};
        
        // Combine data from multiple sources
        const mappedResult = {
          detectedProduct: {
            name: detectionData.name || extractedInfo.productName || shippingInfo.itemDescription || 'Unknown Product',
            brand: detectionData.brand || extractedInfo.brand || '',
            model: detectionData.model || '',
            category: detectionData.category || extractedInfo.category || 'other',
            sku: detectionData.sku || extractedInfo.sku || shippingInfo.sku || '',
            description: detectionData.description || extractedInfo.description || '',
            barcode: extractedInfo.barcode || detectionData.barcode || '',
            poNumber: extractedInfo.poNumber || shippingInfo.poNumber || '',
            trackingCode: extractedInfo.trackingCode || '',
            trip: extractedInfo.trip || shippingInfo.trip || '',
            quantity: extractedInfo.quantity || 1,
            weight: extractedInfo.weight || '',
            cubes: extractedInfo.cubes || '',
            specifications: {
              estimatedPrice: detectionData.pricing?.retail || 0,
              estimatedCost: detectionData.pricing?.cost || 0
            },
            estimatedDimensions: {
              length: detectionData.dimensions?.length || '',
              width: detectionData.dimensions?.width || '',
              height: detectionData.dimensions?.height || ''
            },
            shippingInfo: {
              destination: shippingInfo.destination || extractedInfo.destination || {},
              shipper: shippingInfo.shipper || extractedInfo.shipper || {},
              customerName: shippingInfo.customerName || extractedInfo.customerName || ''
            }
          },
          confidence: responseData.confidence || detectionData.confidence || 0.8,
          detectedFeatures: [],
          positioning: {
            coordinates: { x: 0, y: 0, z: 0 }
          },
          webImages: responseData.webImages || detectionData.images || [],
          textAboveSecondBarcode: extractedInfo.barcode || detectionData.barcode || null,
          uploadedImageUrl: responseData.uploadedImageUrl || null
        };
        // Normalize/suggest furniture category against taxonomy
        try {
          const suggested = suggestFurnitureCategory(mappedResult.detectedProduct);
          const normalized = normalizeCategory(suggested.category || mappedResult.detectedProduct.category);
          mappedResult.detectedProduct.category = normalized;
          console.log('🧭 Category suggestion:', suggested, '-> normalized:', normalized);
        } catch (catErr) {
          console.warn('Category suggestion failed:', catErr);
          mappedResult.detectedProduct.category = normalizeCategory(mappedResult.detectedProduct.category);
        }
        
        setAiDetectionResult(mappedResult);
        
        // Handle existing item found in stock
        if (result.data.existingItem) {
          const existingItem = result.data.existingItem;
          setExistingItem(existingItem);
          setBaseStock(existingItem.quantity || 0);
          
          // Load existing warehouse photos if available
          if (existingItem.warehousePhotos && existingItem.warehousePhotos.length > 0) {
            setExistingPhotos(existingItem.warehousePhotos);
          }
          
          // Set quantity to current stock + 1 (minimum addition)
          setAiFormData(prev => ({
            ...prev,
            quantity: (existingItem.quantity || 0) + 1
          }));
        }
        
        setEditableSku(mappedResult.detectedProduct.sku || ''); // Set editable SKU
        setShowResults(true); // All states are set, now show the results
        
        // Log the mapped detection result
        console.log('🤖 AI Detection Result (mapped):', mappedResult);
        // Handle suggestions and customer info from the response
        if (result.data.suggestions) {
          console.log('📋 Suggestions from backend:', result.data.suggestions);
          setSuggestions(result.data.suggestions);
          // Default linking to matched customer/order if provided
          setLinking({
            customerId: result.data.suggestions?.matchedCustomer?.id || null,
            orderId: result.data.suggestions?.matchedOrder?.id || null
          });
        }
        
        // Extract customer info from shipping info or extractedInfo
        const customerName = result.data?.shippingInfo?.customerName || 
                           result.data?.extractedInfo?.customerName || 
                           result.data?.suggestions?.customerName;
        
        console.log('🔍 Detected customer name:', customerName);
        console.log('🔍 Matched customer:', result.data.suggestions?.matchedCustomer);
        
        if (customerName) {
          // Always pre-fill the form with detected name when no match
          if (!result.data.suggestions?.matchedCustomer) {
            // Parse the customer name
            const name = String(customerName).trim();
            const parts = name.split(/\s+/);
            const firstName = parts[0] || '';
            const lastName = parts.slice(1).join(' ') || '';
            
            console.log('📝 Pre-filling form - First:', firstName, 'Last:', lastName);
            
            // Pre-fill customer form with detected name
            setCustomerForm({
              firstName: firstName,
              lastName: lastName,
              email: '',
              phone: ''
            });
            
            // Update suggestions if needed
            if (!result.data.suggestions?.customerName) {
              const customerSuggestion = {
                isStock: false,
                customerName: customerName,
                skuCandidates: [
                  result.data?.extractedInfo?.sku,
                  result.data?.shippingInfo?.sku,
                  result.data?.detectionResult?.sku
                ].filter(Boolean),
                matchedCustomer: null,
                matchedOrder: null,
                matchReasons: {}
              };
              setSuggestions(prev => ({ ...prev, ...customerSuggestion }));
            }
          }
        }
        
        // Set web images from detection result or fetch them via Google Images
        let webImagesData = mappedResult.webImages || [];
        
        console.log('📸 Web images received:', webImagesData);
        console.log('📸 Full detection response:', result.data);
        
        // If no web images, try to fetch from Google Images API
        if (webImagesData.length === 0 && mappedResult.detectedProduct) {
          const detected = mappedResult.detectedProduct;
          // Include SKU in the search query for better results
          const sku = detected.sku || result.data?.extractedInfo?.sku || result.data?.shippingInfo?.sku;
          const queryParts = [detected.brand, sku, detected.model, detected.name].filter(Boolean);
          const searchQuery = queryParts.join(' ');
          
          if (searchQuery) {
            try {
              console.log('🔍 Fetching Google Images for:', searchQuery);
              const googleImagesResponse = await api.get('/ai-detection/google-images', {
                params: { query: searchQuery }
              });
              
              if (googleImagesResponse.data?.success && googleImagesResponse.data?.images) {
                webImagesData = googleImagesResponse.data.images;
                console.log('✅ Google Images fetched:', webImagesData.length, 'images');
              }
            } catch (imgError) {
              console.log('Google Images fetch failed:', imgError);
            }
          }
        }
        
        if (webImagesData.length > 0) {
          setWebImages(webImagesData);
          
          // Auto-select first 3 web images
          const autoSelectedImages = webImagesData
            .slice(0, 3)
            .map(img => typeof img === 'string' ? img : img.url);
          setSelectedWebImages(autoSelectedImages);
          
          console.log('✅ Auto-selected web images:', autoSelectedImages);
        }
        
        // Set search query for manual search (include SKU)
        const detected = mappedResult.detectedProduct;
        const sku = detected.sku || result.data?.extractedInfo?.sku || result.data?.shippingInfo?.sku;
        const queryParts = [detected.brand, sku, detected.model, detected.name].filter(Boolean);
        const searchQuery = queryParts.join(' ');
        setCurrentSearchQuery(searchQuery);
        setCustomSearchQuery(searchQuery);
        
        // Pre-fill form with enhanced detected data
        
        const existing = result.data.existingItem;
        setAiFormData(prev => ({
          ...prev,
          pricing: {
            // Use existing item's cost if available, otherwise use AI detected cost
            cost: (existing && existing.cost) 
              ? parseFloat(existing.cost) || ''
              : parseFloat(String(detected.specifications?.estimatedCost || '0').replace(/[$,]/g, '')) || '',
            // Use existing item's retail price if available, otherwise use AI detected price
            retail: (existing && existing.retail) 
              ? parseFloat(existing.retail) || ''
              : parseFloat(String(detected.specifications?.estimatedPrice || '0').replace(/[$,]/g, '')) || ''
          },
          positioning: {
            x: mappedResult.positioning?.coordinates?.x || 0,
            y: mappedResult.positioning?.coordinates?.y || 0,
            z: mappedResult.positioning?.coordinates?.z || 0
          },
          boxDimensions: {
            length: detected.estimatedDimensions?.length || '',
            width: detected.estimatedDimensions?.width || '',
            height: detected.estimatedDimensions?.height || ''
          }
        }));
      } else {
        throw new Error(result.message || 'AI furniture detection failed');
      }
    } catch (error) {
      console.error('AI Detection Error:', error);
      console.log('AI Detection Error Response:', error.response?.data);
      alert(`AI detection failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setAiDetecting(false);
    }
  };

  const handleToggleWebImage = (imageUrl) => {
    setSelectedWebImages(prev => 
      prev.includes(imageUrl) 
        ? prev.filter(url => url !== imageUrl) 
        : [...prev, imageUrl]
    );
  };

  const handleDeleteWebImage = (imageUrl, event) => {
    event.stopPropagation();
    setWebImages(prev => prev.filter(img => (typeof img === 'string' ? img !== imageUrl : img.url !== imageUrl)));
    setSelectedWebImages(prev => prev.filter(url => url !== imageUrl));
  };

  const performSkuSearch = async (sku) => {
    if (!sku.trim() || !selectedLocation?._id) {
      return;
    }

    console.log(`[SKU Search] Starting search for SKU: "${sku}" in location: "${selectedLocation.name}"`);

    try {
      // Search for existing inventory item with this SKU
      const inventoryResponse = await api.get(`/inventory`, {
        params: {
          locationId: selectedLocation._id,
          search: sku.trim()
        }
      });
      console.log('[SKU Search] Inventory API Response:', inventoryResponse.data);

      if (inventoryResponse.data?.success && inventoryResponse.data?.data?.items) {
        // The backend already filtered by `contains`, so we can trust the first result.
        const matchingItem = inventoryResponse.data.data.items[0];
        console.log('[SKU Search] Matching inventory item:', matchingItem);
        
        if (matchingItem) {
          setExistingItem(matchingItem);
          setAiFormData(prev => ({
            ...prev,
            pricing: {
              cost: matchingItem.cost || '',
              retail: matchingItem.retail || ''
            }
          }));
        } else {
          setExistingItem(null);
        }
      }

      // Search for customer orders with this SKU
      const ordersResponse = await api.get(`/orders/search-by-sku`, {
        params: {
          locationId: selectedLocation._id,
          sku: sku.trim()
        }
      });
      console.log('[SKU Search] Orders API Response:', ordersResponse.data);

      if (ordersResponse.data?.success && ordersResponse.data?.data?.orders) {
        const ordersWithSku = ordersResponse.data.data.orders;
        if (ordersWithSku.length > 0) {
          const firstOrder = ordersWithSku[0];
          if (firstOrder.customer) {
            const customerSuggestion = {
              isStock: false,
              customerName: `${firstOrder.customer.firstName} ${firstOrder.customer.lastName}`,
              skuCandidates: [sku.trim()],
              matchedCustomer: firstOrder.customer,
              matchedOrder: firstOrder,
              matchReasons: {
                customer: `Found order with SKU ${sku}`,
                order: `Order #${firstOrder.orderNumber} contains this SKU`
              }
            };
            setSuggestions(customerSuggestion);
            setLinking({ customerId: firstOrder.customer.id, orderId: firstOrder.id });
          }
        } else {
          console.log('[SKU Search] No matching orders found. Suggesting as stock.');
          setSuggestions({
            isStock: true,
            customerName: '',
            skuCandidates: [sku.trim()],
            matchedCustomer: null,
            matchedOrder: null,
            matchReasons: {}
          });
          setLinking({ customerId: null, orderId: null });
        }
      }
    } catch (error) {
      console.error('Error searching for SKU:', error);
    }
  };

  const handleSkuChange = async (newSku) => {
    setEditableSku(newSku);
    
    // Update the SKU in the aiDetectionResult that will be saved to database
    setAiDetectionResult(prev => ({
      ...prev,
      detectedProduct: {
        ...prev.detectedProduct,
        sku: newSku
      }
    }));
    
    await performSkuSearch(newSku);
  };

  const handleSkuRefresh = async () => {
    console.log('Refresh button clicked, SKU:', editableSku);
    console.log('Selected Location:', selectedLocation);
    if (!selectedLocation?._id) {
      console.error('No selected location ID available for SKU search');
      alert('Cannot refresh SKU data: No location selected');
      return;
    }
    await performSkuSearch(editableSku);
    // Force-refresh Ashley data scrape box
    setScrapeRefreshTick((t) => t + 1);
    // Clear search to force Google Images refresh
    setCurrentSearchQuery('');
    console.log('[SKU Refresh] Triggered Ashley re-scrape and cleared search query');
  };

  const handleCustomImageSearch = async () => {
    if (!customSearchQuery.trim()) {
      alert('Please enter a search query');
      return;
    }

    setIsSearchingImages(true);
    
    try {
      const { data: result } = await api.post('/ai-detection/google-images', {
        searchQuery: customSearchQuery.trim()
      });
      
      if (result.success && result.data.images) {
        // Get current image URLs to prevent duplicates
        const existingUrls = webImages.map(img => img.url);
        
        // Filter out duplicates
        const newImages = result.data.images.filter(img => !existingUrls.includes(img.url));
        
        if (newImages.length > 0) {
          setWebImages(prev => [...prev, ...newImages]);
          setCurrentSearchQuery(customSearchQuery.trim());
        } else {
          alert('No new images found or all results are already in the gallery.');
        }
      } else {
        throw new Error(result.message || 'Image search failed');
      }
    } catch (error) {
      console.error('Custom image search error:', error);
      alert('Failed to search for images. Please try again.');
    } finally {
      setIsSearchingImages(false);
    }
  };

  // Manual SKU search function
  const handleManualSkuSearch = async () => {
    if (!manualSku.trim()) {
      alert('Please enter a SKU to search');
      return;
    }

    setIsManualSearching(true);
    setAiDetecting(true);
    
    try {
      console.log('[Manual Mode] Starting manual SKU search for:', manualSku);
      
      // Create a minimal detection result based on SKU only
      const mappedResult = {
        detectedProduct: {
          name: '',
          brand: '',
          model: '',
          category: 'furniture',
          sku: manualSku.trim(),
          description: '',
          barcode: '',
          poNumber: '',
          trackingCode: '',
          trip: '',
          quantity: 1,
          weight: '',
          cubes: '',
          specifications: {
            estimatedPrice: 0,
            estimatedCost: 0
          },
          estimatedDimensions: {
            length: '',
            width: '',
            height: ''
          },
          shippingInfo: {
            destination: {},
            shipper: {},
            customerName: ''
          }
        },
        confidence: 1.0,
        detectedFeatures: [],
        positioning: {
          coordinates: { x: 0, y: 0, z: 0 }
        },
        webImages: [],
        textAboveSecondBarcode: null,
        uploadedImageUrl: null
      };
      
      setAiDetectionResult(mappedResult);
      setEditableSku(manualSku.trim());
      
      // Perform SKU search in inventory and orders
      await performSkuSearch(manualSku.trim());
      
      // Set as stock by default for manual mode
      setSuggestions({
        isStock: true,
        customerName: '',
        skuCandidates: [manualSku.trim()],
        matchedCustomer: null,
        matchedOrder: null,
        matchReasons: {}
      });
      
      setShowResults(true);
      
      // Force Ashley data scrape to run immediately
      setScrapeRefreshTick(prev => prev + 1);
      
      console.log('[Manual Mode] Manual SKU search completed');
      
    } catch (error) {
      console.error('Manual SKU search error:', error);
      alert('Failed to search for SKU. Please try again.');
    } finally {
      setIsManualSearching(false);
      setAiDetecting(false);
    }
  };

  // Product URL will be sourced from Ashley data (scrapedProductData) only.

  const handleAiFormSubmit = async () => {
    if (!aiDetectionResult || !aiFormData.pricing.retail) {
      alert('Please ensure all required fields are filled.');
      return;
    }

    console.log('🔍 AI Form Submit Debug:');
    console.log('aiCapturedImage:', aiCapturedImage);
    console.log('selectedWebImages:', selectedWebImages);
    console.log('webImages:', webImages);

    // Create FormData for file upload
    const formData = new FormData();

    // Add the captured image file only when creating a new item and not in manual mode
    // If we detected an existing item, skip uploading images to preserve current gallery
    if (!existingItem && !manualMode) {
      if (aiCapturedImage?.blob) {
        console.log('✅ Adding shipping label image to FormData:', aiCapturedImage.blob);
        formData.append('images', aiCapturedImage.blob, 'shipping-label.jpg');
      } else {
        console.log('ℹ️ No captured image blob found (manual mode or no image)');
      }

      // Add warehouse location photos (only new ones, not existing)
      if (warehousePhotos && warehousePhotos.length > 0) {
        warehousePhotos.forEach((photo, index) => {
          if (photo && photo.blob && !photo.isExisting) {
            console.log(`✅ Adding warehouse photo ${index + 1} to FormData:`, photo.blob);
            formData.append('images', photo.blob, `warehouse-location-${index + 1}.jpg`);
          }
        });
      } else {
        console.log('ℹ️ No warehouse photos to upload');
      }
    } else {
      // For existing items, still add new warehouse photos
      if (warehousePhotos && warehousePhotos.length > 0) {
        warehousePhotos.forEach((photo, index) => {
          if (photo && photo.blob && !photo.isExisting) {
            console.log(`✅ Adding new warehouse photo ${index + 1} to existing item:`, photo.blob);
            formData.append('images', photo.blob, `warehouse-location-${index + 1}.jpg`);
          }
        });
      }
      console.log('ℹ️ Existing item detected, only uploading new warehouse photos');
    }

    // Add form data as JSON
    const shouldLinkToOrder = (placementMode === 'customer') && !!(linking.customerId || linking.orderId);
    const submitData = {
      locationId: selectedLocation?._id || null,
      detectedProduct: aiDetectionResult.detectedProduct,
      quantity: parseInt(aiFormData.quantity),
      pricing: {
        cost: parseFloat(aiFormData.pricing.cost) || 0,
        retail: parseFloat(aiFormData.pricing.retail)
      },
      existingItemId: existingItem?._id || existingItem?.id || null,
      positioning: aiFormData.positioning,
      boxDimensions: {
        length: parseFloat(aiFormData.boxDimensions.length) || 0,
        width: parseFloat(aiFormData.boxDimensions.width) || 0,
        height: parseFloat(aiFormData.boxDimensions.height) || 0
      },
      aiDetectionData: {
        sourceImage: aiCapturedImage?.url || '',
        confidence: aiDetectionResult.confidence || 0.8,
        detectedFeatures: aiDetectionResult.detectedFeatures || [],
        // Only send selected web images when creating a new item
        webImages: existingItem ? [] : selectedWebImages,
        gpsLocation: null, // GPS location not available in this context
        timestamp: new Date().toISOString(),
        // Include scraped product data
        scrapedData: scrapedProductData,
        // Mark as manual entry if in manual mode
        isManualEntry: manualMode
      },
      // Send customerId when placement mode is customer
      customerId: (placementMode === 'customer' && linking.customerId) ? linking.customerId : null,
      orderId: shouldLinkToOrder ? (linking.orderId || null) : null,
      linkToOrder: shouldLinkToOrder,
      // Placement selection
      locationType: placementMode || (suggestions?.isStock ? 'stock' : 'customer'),
      // Include product URL only if provided by Ashley data
      productUrl: scrapedProductData?.productUrl || null
    };
    
    // Debug logging
    console.log('📤 Submitting to backend:', {
      placementMode,
      locationType: submitData.locationType,
      customerId: submitData.customerId,
      linkingCustomerId: linking.customerId,
      existingItemId: submitData.existingItemId
    });

    formData.append('data', JSON.stringify(submitData));

    await onSubmit(formData);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CameraAltIcon />
            AI Add Item
          </Box>
        </DialogTitle>
        <DialogContent>
          {!aiCapturedImage && !aiDetecting && !manualMode && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" gutterBottom>
                Add Furniture Item
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Choose how to add your furniture item
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<CameraAltIcon />}
                  onClick={startAiCamera}
                >
                  Take Photo
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<CloudUploadIcon />}
                  onClick={() => {
                    fileInputRef.current?.click();
                  }}
                >
                  Upload Image
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<EditIcon />}
                  onClick={() => setManualMode(true)}
                  color="secondary"
                >
                  Enter SKU
                </Button>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                📱 AI will identify furniture from photos or you can manually enter the SKU
              </Typography>
              
              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </Box>
          )}
          
          {/* Manual SKU Entry Mode */}
          {manualMode && !aiDetecting && !showResults && (
            <Box sx={{ py: 4 }}>
              <Typography variant="h6" gutterBottom align="center">
                Manual SKU Entry
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Enter the product SKU to fetch details from Ashley furniture inventory
              </Typography>
              
              <Box sx={{ maxWidth: 500, mx: 'auto' }}>
                <TextField
                  fullWidth
                  label="Product SKU"
                  value={manualSku}
                  onChange={(e) => setManualSku(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleManualSkuSearch();
                    }
                  }}
                  placeholder="e.g., B3134-46"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleManualSkuSearch}
                          disabled={!manualSku.trim() || isManualSearching}
                        >
                          <SearchIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    variant="contained"
                    onClick={handleManualSkuSearch}
                    disabled={!manualSku.trim() || isManualSearching}
                    startIcon={isManualSearching ? <CircularProgress size={16} /> : <SearchIcon />}
                  >
                    {isManualSearching ? 'Searching...' : 'Search SKU'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setManualMode(false);
                      setManualSku('');
                    }}
                  >
                    Back
                  </Button>
                </Box>
                
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'center' }}>
                  💡 Enter the exact SKU from the shipping label or product tag
                </Typography>
              </Box>
            </Box>
          )}

          {/* Camera View */}
          {aiCameraOpen && (
            <CameraCapture
              open={aiCameraOpen}
              onClose={() => setAiCameraOpen(false)}
              onCapture={({ blob, url }) => {
                setAiCapturedImage({ blob, url });
                setAiCameraOpen(false);
                performAiDetection(blob, gpsLocation);
              }}
            />
          )}

          {/* AI Detection Loading */}
          {aiDetecting && <LoadingAnalysis />}

          {/* AI Detection Results */}
          {showResults && aiDetectionResult && (
            <Box sx={{ mt: 2 }}>
              {/* Existing Item Alert */}
              {existingItem && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">
                    Similar item found: {existingItem.name}
                  </Typography>
                  <Typography variant="body2">
                    This will update the stock quantity of the existing item.
                  </Typography>
                </Alert>
              )}

              {/* Top Row: Image and Detection Info */}
              <Box sx={{ 
                display: 'flex', 
                gap: 3, 
                alignItems: 'flex-start', 
                mt: 2,
                flexDirection: { xs: 'column', md: 'row' }
              }}>
                {/* Left Column - Image */}
                {aiCapturedImage && (
                  <Box sx={{ 
                    flexShrink: 0,
                    width: { xs: '100%', md: '40%' }
                  }}>
                    <img
                      src={aiCapturedImage.url}
                      alt="Captured product"
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        height: 'auto',
                        aspectRatio: '1/1',
                        borderRadius: '8px',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                )}

                {/* Right Column - AI Detection Info Only */}
                <Box sx={{ 
                  flexGrow: 1,
                  width: { xs: '100%', md: '60%' }
                }}>
                  <DetectionInfo 
                    aiDetectionResult={aiDetectionResult} 
                    editableSku={editableSku}
                    handleSkuChange={handleSkuChange}
                    handleSkuRefresh={handleSkuRefresh}
                    productUrl={scrapedProductData?.productUrl || ''}
                  />
                </Box>
              </Box>

              {/* Full Width Row: Web Store Data */}
              <Box sx={{ mt: 3 }}>
                <ScrapedProductInfo 
                  sku={editableSku}
                  refreshToken={scrapeRefreshTick}
                  onDataUpdate={(scrapedData) => {
                    if (scrapedData) {
                      console.log('[AI Add Item] Received scraped data:', scrapedData);
                      console.log('[AI Add Item] Scraped title:', scrapedData.title);
                      console.log('[AI Add Item] Scraped description:', scrapedData.description);
                      setScrapedProductData(scrapedData);
                      
                      // Don't update here - let the useEffect handle it to avoid duplicate updates
                      console.log('[AI Add Item] Ashley data received, will sync via useEffect');
                      
                      // Auto-populate retail price and cost from CSV data
                      if (scrapedData.price && scrapedData.price !== 'Price not available') {
                        const priceMatch = scrapedData.price.match(/[\d,]+\.?\d*/);
                        if (priceMatch) {
                          const numericPrice = priceMatch[0].replace(/,/g, '');
                          setAiFormData(prev => ({
                            ...prev,
                            pricing: { 
                              ...prev.pricing, 
                              retail: numericPrice,
                              cost: scrapedData.cost || prev.pricing.cost
                            }
                          }));
                        }
                      }
                      
                      // Reset query to force Google Images effect to run with updated title/model
                      setCurrentSearchQuery('');
                    }
                  }}
                />
              </Box>

              {/* Two Column Row: Customer Section and Pricing */}
              <Box sx={{ 
                display: 'flex', 
                gap: 3, 
                alignItems: 'flex-start', 
                mt: 3,
                flexDirection: { xs: 'column', md: 'row' }
              }}>
                {/* Left Column - Customer Section */}
                <Box sx={{ 
                  flexGrow: 1,
                  width: { xs: '100%', md: '50%' }
                }}>
                  <CustomerStockSection
                    suggestions={suggestions}
                    linking={linking}
                    setLinking={setLinking}
                    customerForm={customerForm}
                    setCustomerForm={setCustomerForm}
                    createCustomer={createCustomer}
                    selectedLocation={selectedLocation}
                    detectedSku={aiDetectionResult?.detectedProduct?.sku}
                    placementMode={placementMode}
                    setPlacementMode={setPlacementMode}
                  />
                </Box>

                {/* Right Column - Pricing Section */}
                <Box sx={{ 
                  flexGrow: 1,
                  width: { xs: '100%', md: '50%' }
                }}>
                  <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom>
                      Product Pricing
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Quantity"
                          type="number"
                          value={aiFormData.quantity}
                          onChange={(e) => setAiFormData(prev => ({
                            ...prev,
                            quantity: Math.max(baseStock + 1, parseInt(e.target.value) || 1)
                          }))}
                          inputProps={{ min: baseStock + 1 }}
                          helperText={baseStock > 0 ? `Minimum: ${baseStock + 1} (Current stock: ${baseStock})` : ''}
                          required
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Retail Price"
                          type="number"
                          value={aiFormData.pricing.retail}
                          onChange={(e) => setAiFormData(prev => ({
                            ...prev,
                            pricing: { ...prev.pricing, retail: e.target.value }
                          }))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            readOnly: !!existingItem,
                          }}
                          disabled={!!existingItem}
                          required
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Cost Price"
                          type="number"
                          value={aiFormData.pricing.cost}
                          onChange={(e) => setAiFormData(prev => ({
                            ...prev,
                            pricing: { ...prev.pricing, cost: e.target.value }
                          }))}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </Box>

              {/* Full Width Row: Product Image Gallery */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  📦 Inventory Gallery
                  <Typography variant="caption" color="text.secondary">
                    (Product images for inventory)
                  </Typography>
                </Typography>
                <ProductImages
                  aiCapturedImage={null} // Exclude the label image
                  webImages={webImages}
                  selectedWebImages={selectedWebImages}
                  isSearchingImages={isSearchingImages}
                  currentSearchQuery={currentSearchQuery}
                  customSearchQuery={customSearchQuery}
                  setCustomSearchQuery={setCustomSearchQuery}
                  onSearch={handleCustomImageSearch}
                  onToggle={handleToggleWebImage}
                  onDelete={handleDeleteWebImage}
                />
              </Box>

              {/* Warehouse Location Photos */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  📍 Warehouse Location
                  <Typography variant="caption" color="text.secondary">
                    (Optional - Photos of where item is stored)
                  </Typography>
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CameraAltIcon />
                    Warehouse Photos
                    {baseStock > 0 && (
                      <Typography variant="caption" color="primary" sx={{ ml: 1 }}>
                        (Base Stock: {baseStock}, Adding: {Math.max(0, requiredPhotos - baseStock)})
                      </Typography>
                    )}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {/* Photo Slots */}
                    {Array.from({ length: requiredPhotos }).map((_, slotIndex) => {
                      const photo = warehousePhotos[slotIndex];
                      const isExistingSlot = slotIndex < baseStock;
                      const isEmpty = !photo;
                      
                      return (
                        <Box
                          key={slotIndex}
                          onClick={() => !isExistingSlot && isEmpty ? openCameraForSlot(slotIndex) : null}
                          sx={{
                            width: 120,
                            height: 120,
                            border: '2px solid',
                            borderColor: isExistingSlot ? 'success.main' : isEmpty ? 'grey.300' : 'primary.main',
                            borderStyle: isEmpty && !isExistingSlot ? 'dashed' : 'solid',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: !isExistingSlot && isEmpty ? 'pointer' : 'default',
                            bgcolor: isExistingSlot ? 'success.50' : isEmpty ? 'grey.50' : 'transparent',
                            transition: 'all 0.2s',
                            position: 'relative',
                            overflow: 'hidden',
                            '&:hover': !isExistingSlot && isEmpty ? {
                              borderColor: 'primary.main',
                              bgcolor: 'primary.50'
                            } : {}
                          }}
                        >
                          {isEmpty ? (
                            <>
                              <CameraAltIcon sx={{ 
                                color: isExistingSlot ? 'success.main' : 'grey.500', 
                                fontSize: 24 
                              }} />
                              <Typography variant="caption" color={isExistingSlot ? 'success.main' : 'text.secondary'} textAlign="center">
                                {isExistingSlot ? 'Existing' : `Slot ${slotIndex + 1}`}
                              </Typography>
                            </>
                          ) : (
                            <>
                              <img
                                src={photo.url}
                                alt={`Warehouse ${slotIndex + 1}`}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                              {!photo.isExisting && (
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (photo.url && !photo.isExisting) {
                                      URL.revokeObjectURL(photo.url);
                                    }
                                    setWarehousePhotos(prev => {
                                      const newPhotos = [...prev];
                                      newPhotos[slotIndex] = null;
                                      return newPhotos;
                                    });
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: 2,
                                    right: 2,
                                    bgcolor: 'rgba(0,0,0,0.5)',
                                    color: 'white',
                                    width: 24,
                                    height: 24,
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 2,
                                  left: 2,
                                  bgcolor: photo.isExisting ? 'success.main' : 'info.main',
                                  color: 'white',
                                  px: 0.5,
                                  py: 0.25,
                                  borderRadius: 0.5,
                                  fontSize: '0.6rem',
                                }}
                              >
                                #{slotIndex + 1} {photo.isExisting && '✓'}
                              </Box>
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    📸 Optionally take {Math.max(0, requiredPhotos - baseStock)} photo{Math.max(0, requiredPhotos - baseStock) !== 1 ? 's' : ''} of where the new items will be stored
                    {baseStock > 0 && (
                      <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                        {' '}• {baseStock} existing location{baseStock !== 1 ? 's' : ''} shown
                      </span>
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        {/* Warehouse Camera Modal */}
        <Dialog
          open={warehouseCameraOpen}
          onClose={closeWarehouseCamera}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CameraAltIcon />
                Take Warehouse Photo
              </Box>
              <Typography variant="body2" color="primary" fontWeight="bold">
                Slot {activeSlotIndex !== null ? activeSlotIndex + 1 : 1}
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <video
                ref={warehouseVideoRef}
                autoPlay
                playsInline
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: 'auto',
                  borderRadius: '8px'
                }}
              />
              <canvas
                ref={warehouseCanvasRef}
                style={{ display: 'none' }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeWarehouseCamera}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={captureWarehousePhoto}
              startIcon={<CameraAltIcon />}
            >
              Capture
            </Button>
          </DialogActions>
        </Dialog>

        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button onClick={handleClose} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Cancel
          </Button>
          {aiDetectionResult && (
            <>
              {isMarkingReceivedForOrder ? (
                // When marking item as received for order, only show Close button
                <Button
                  variant="contained"
                  onClick={handleClose}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Close
                </Button>
              ) : (
                // Normal add/update item flow
                <Button
                  variant="contained"
                  onClick={handleAiFormSubmit}
                  disabled={isSubmitting || !aiFormData.pricing.retail}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {isSubmitting ? 'Adding...' : 
                   placementMode === 'customer' ? 'Add to Customer' :
                   existingItem ? 'Update Stock' : 'Add Item'}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Warehouse Camera Modal */}
      <Dialog
        open={warehouseCameraOpen}
        onClose={closeWarehouseCamera}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CameraAltIcon />
              Take Warehouse Photo
            </Box>
            <Typography variant="body2" color="primary" fontWeight="bold">
              Photo {currentPhotoIndex + 1} of {requiredPhotos}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <video
              ref={warehouseVideoRef}
              autoPlay
              playsInline
              style={{
                width: '100%',
                maxWidth: '400px',
                height: 'auto',
                borderRadius: '8px'
              }}
            />
            <canvas
              ref={warehouseCanvasRef}
              style={{ display: 'none' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeWarehouseCamera}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={captureWarehousePhoto}
            startIcon={<CameraAltIcon />}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden canvas no longer needed; handled by CameraCapture */}
    </>
  );
};

export default AiAddItemModal;
